// Pure pixel-math + cluster-detection helpers for the dual-tier milestone timeline.

import type { Milestone } from "../../../../types";

const MS_PER_DAY = 86_400_000;

/**
 * The date a milestone's dot occupies on the timeline. The timeline's job is to
 * show current and upcoming milestones with their status at a glance, so a dot
 * has only two resting places:
 *   - its planned date, while still upcoming (not yet delivered), and
 *   - its actual delivery date, once delivered.
 *
 * It does NOT advance to the acceptance or payment date — those later lifecycle
 * states are conveyed by the dot's colour + label (and on the detail panel),
 * not by moving the dot forward. This keeps the rule consistent for every
 * milestone: a delivered-late dot stays pinned to where the slippage happened
 * (rather than drifting onto a later acceptance date and reading as "delivered
 * late today"), and an on-time dot stays on its real delivery position rather
 * than wandering forward to acceptance/payment.
 */
export function effectiveTimelineDate(m: Milestone): string | undefined {
  if (m.deliveredAt) return m.deliveredAt;
  return m.plannedDate || undefined;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface MonthMarker {
  iso: string;
  label: string;
  fraction: number;
}

export type TimelineEntry =
  | { kind: "single"; milestone: Milestone; centreIso: string; fraction: number }
  | {
      kind: "cluster";
      milestones: Milestone[];
      centreIso: string;
      fraction: number;
    };

function isoDayUTC(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function fromUTCms(ms: number): string {
  const d = new Date(ms);
  return d.toISOString().slice(0, 10);
}

function clampFraction(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Whole-day delta from→to (negative when to < from). */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((isoDayUTC(toIso) - isoDayUTC(fromIso)) / MS_PER_DAY);
}

/** YYYY-MM-DD validity (lex check + parse round-trip). */
export function isIsoDate(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return !Number.isNaN(isoDayUTC(value));
}

/**
 * Return the active window's [start, end] dates centred on `focus` with a
 * span of `windowDays`. Clamped to the contract span on either side. When
 * `focus` is null/invalid/out-of-span the caller is expected to fall back
 * to today; this helper centres on whatever ISO day is passed in.
 */
export function windowRange(
  focusIso: string,
  windowDays: number,
  contractStart: string,
  contractEnd: string,
): DateRange {
  const focusMs = isoDayUTC(focusIso);
  const half = Math.floor(windowDays / 2);
  let startMs = focusMs - half * MS_PER_DAY;
  let endMs = startMs + (windowDays - 1) * MS_PER_DAY;
  const cStart = isoDayUTC(contractStart);
  const cEnd = isoDayUTC(contractEnd);
  if (startMs < cStart) {
    startMs = cStart;
    endMs = Math.min(cEnd, startMs + (windowDays - 1) * MS_PER_DAY);
  }
  if (endMs > cEnd) {
    endMs = cEnd;
    startMs = Math.max(cStart, endMs - (windowDays - 1) * MS_PER_DAY);
  }
  return { start: fromUTCms(startMs), end: fromUTCms(endMs) };
}

export function positionFraction(
  iso: string,
  windowStart: string,
  windowEnd: string,
): number {
  const ms = isoDayUTC(iso);
  const start = isoDayUTC(windowStart);
  const end = isoDayUTC(windowEnd);
  const span = end - start;
  if (span <= 0) return 0.5;
  return clampFraction((ms - start) / span);
}

/** Returns true when `iso` falls within [windowStart, windowEnd] inclusive. */
export function isWithinWindow(
  iso: string,
  windowStart: string,
  windowEnd: string,
): boolean {
  const ms = isoDayUTC(iso);
  return ms >= isoDayUTC(windowStart) && ms <= isoDayUTC(windowEnd);
}

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Month start markers within the window (inclusive of the window start). */
export function monthMarkers(
  windowStart: string,
  windowEnd: string,
): MonthMarker[] {
  const start = new Date(`${windowStart}T00:00:00Z`);
  const end = new Date(`${windowEnd}T00:00:00Z`);
  const out: MonthMarker[] = [];
  let cursor = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1),
  );
  // First marker may be before windowStart; advance one month if so.
  if (cursor.getTime() < start.getTime()) {
    cursor = new Date(
      Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1),
    );
  }
  while (cursor.getTime() <= end.getTime()) {
    const iso = cursor.toISOString().slice(0, 10);
    out.push({
      iso,
      label: MONTH_LABELS[cursor.getUTCMonth()],
      fraction: positionFraction(iso, windowStart, windowEnd),
    });
    cursor = new Date(
      Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1),
    );
  }
  return out;
}

interface ClusterableMilestone {
  milestone: Milestone;
  iso: string;
  fraction: number;
}

/**
 * Greedy single-pass cluster detection. Milestones whose adjacent fractional
 * positions are within `threshold` collapse into a single cluster entry. The
 * cluster's centre fraction is the mean of its members. Threshold defaults
 * to 5% per po-decisions §4 and is configurable so the value can be tuned
 * once representative customer data lands.
 */
export function clusterMilestones(
  milestones: readonly Milestone[],
  windowStart: string,
  windowEnd: string,
  threshold = 0.05,
): TimelineEntry[] {
  const visible: ClusterableMilestone[] = [];
  for (const m of milestones) {
    const iso = effectiveTimelineDate(m);
    if (!iso) continue;
    if (!isWithinWindow(iso, windowStart, windowEnd)) continue;
    visible.push({
      milestone: m,
      iso,
      fraction: positionFraction(iso, windowStart, windowEnd),
    });
  }
  visible.sort((a, b) => a.fraction - b.fraction);

  const out: TimelineEntry[] = [];
  let bucket: ClusterableMilestone[] = [];
  for (const item of visible) {
    if (bucket.length === 0) {
      bucket = [item];
      continue;
    }
    const last = bucket[bucket.length - 1];
    if (item.fraction - last.fraction <= threshold) {
      bucket.push(item);
    } else {
      out.push(flushBucket(bucket));
      bucket = [item];
    }
  }
  if (bucket.length > 0) out.push(flushBucket(bucket));
  return out;
}

function flushBucket(bucket: ClusterableMilestone[]): TimelineEntry {
  if (bucket.length === 1) {
    const only = bucket[0];
    return {
      kind: "single",
      milestone: only.milestone,
      centreIso: only.iso,
      fraction: only.fraction,
    };
  }
  let total = 0;
  for (const item of bucket) total += item.fraction;
  const mean = total / bucket.length;
  // Pick the date closest to the cluster mean as the canonical centre iso.
  let closest = bucket[0];
  for (const item of bucket) {
    if (
      Math.abs(item.fraction - mean) < Math.abs(closest.fraction - mean)
    ) {
      closest = item;
    }
  }
  return {
    kind: "cluster",
    milestones: bucket.map((b) => b.milestone),
    centreIso: closest.iso,
    fraction: mean,
  };
}
